import { io, type Socket } from "socket.io-client";
import type { CaseMessage } from "./api";

export type CaseUnreadChangedEvent = {
  type: "case.unread.changed.v1";
  caseId: string;
  reason: string;
  changeId: string;
};

export type CaseRealtimeHandlers = {
  onMessage: (event: { caseId: string; message: CaseMessage }) => void;
  onExternalStatus: (event: { caseId: string }) => void;
  onUnreadChanged: (event: CaseUnreadChangedEvent) => void;
  onReconnect: () => void;
  onDisconnected: () => void;
  onAccessRevoked: () => void;
};

type RealtimeConsumer = Partial<CaseRealtimeHandlers> & { caseId?: string };
const consumers = new Set<RealtimeConsumer>();
let sharedSocket: Socket | null = null;

function csrfToken(): string | undefined {
  const encoded = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith("crm_session_csrf="))
    ?.split("=")[1];
  return encoded ? decodeURIComponent(encoded) : undefined;
}

function subscribe(socket: Socket, consumer: RealtimeConsumer) {
  if (!consumer.caseId) {
    consumer.onReconnect?.();
    return;
  }
  socket.emit(
    "case.subscribe",
    { caseId: consumer.caseId },
    (result: { ok?: boolean }) => {
      if (!consumers.has(consumer)) return;
      if (result?.ok) consumer.onReconnect?.();
      else consumer.onAccessRevoked?.();
    },
  );
}

function ensureSocket(): Socket | null {
  if (sharedSocket) return sharedSocket;
  const csrf = csrfToken();
  if (!csrf) {
    return null;
  }
  const socket: Socket = io("/case-messages", {
    path: "/api/v1/realtime/socket.io",
    withCredentials: true,
    auth: { csrf },
    transports: ["websocket", "polling"],
    reconnection: true,
  });
  sharedSocket = socket;
  socket.on("connect", () => {
    for (const consumer of consumers) subscribe(socket, consumer);
  });
  socket.on(
    "case.message",
    (event: { caseId: string; message: CaseMessage }) => {
      for (const consumer of consumers) consumer.onMessage?.(event);
    },
  );
  socket.on("case.external-status", (event: { caseId: string }) => {
    for (const consumer of consumers) consumer.onExternalStatus?.(event);
  });
  socket.on("case.unread-changed", (event: CaseUnreadChangedEvent) => {
    for (const consumer of consumers) consumer.onUnreadChanged?.(event);
  });
  socket.on("disconnect", () => {
    for (const consumer of consumers) consumer.onDisconnected?.();
  });
  socket.on("case.access-revoked", (event?: { caseId?: string }) => {
    for (const consumer of consumers) {
      if (!event?.caseId || consumer.caseId === event.caseId)
        consumer.onAccessRevoked?.();
    }
  });
  return socket;
}

function connect(consumer: RealtimeConsumer): () => void {
  if (import.meta.env.MODE === "test") return () => undefined;
  consumers.add(consumer);
  const socket = ensureSocket();
  if (!socket) {
    consumer.onDisconnected?.();
    consumers.delete(consumer);
    return () => undefined;
  }
  if (socket.connected) subscribe(socket, consumer);
  return () => {
    consumers.delete(consumer);
    if (consumer.caseId && socket.connected) socket.emit("case.unsubscribe");
    if (consumers.size === 0) {
      socket.disconnect();
      if (sharedSocket === socket) sharedSocket = null;
    }
  };
}

export function connectCaseRealtime(
  caseId: string,
  handlers: CaseRealtimeHandlers,
): () => void {
  return connect({ caseId, ...handlers });
}

export function connectCaseUnreadRealtime(handlers: {
  onUnreadChanged: (event: CaseUnreadChangedEvent) => void;
  onReconnect?: () => void;
  onDisconnected?: () => void;
}): () => void {
  return connect(handlers);
}

export function resetCaseRealtimeConnection(): void {
  sharedSocket?.disconnect();
  sharedSocket = null;
  consumers.clear();
}
